import React from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import {
  CheckCircle2,
  Circle,
  MapPin,
  Camera,
  SplitSquareHorizontal,
  Hash,
  Layers,
  ChevronsDown,
  ChevronDown,
  ChevronUp,
  Package2,
  Trash2,
  Pencil,
  TriangleAlert
} from 'lucide-react';
import { PalletSummaryDto } from '../../../../types/picking';
import { PickingLine } from './types';
import { getStructureCapNote, hasStructureDeficit } from './structureCaps';
import { getUniformPalletQuantity } from './palletHelpers';

/** Holdbarhet vises kort (lokalt datoformat); full ISO-verdi er ikke nyttig for operatøren. */
function formatExpiryDate(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

interface PickingLineItemProps {
  line: PickingLine;
  index: number;
  isSelected: boolean;
  isActive: boolean;
  pallets: PalletSummaryDto[];
  editingLocation: number | null;
  editingQuantity: number | null;
  newLocation: string;
  newQuantity: string;
  showPalletInfo: string | null;
  onLineClick: (index: number) => void;
  onLocationEdit: (lineNo: number, location: string) => void;
  onQuantityEdit: (lineNo: number, quantity: number) => void;
  onStartLocationEdit: (lineNo: number, location: string) => void;
  onStartQuantityEdit: (lineNo: number, quantity: string) => void;
  onLocationChange: (value: string) => void;
  onQuantityChange: (value: string) => void;
  onSplitLine: (lineNo: number) => void;
  onPickLine: (lineNo: number, qty: number) => void;
  onUndoPicking: (lineNo: number) => void;
  onShowPalletInfo: (palletId: string | null) => void;
  /** Flytt linja nederst i plukkrekkefølgen (persisteres via samme sti som drag). */
  onMoveToEnd?: (lineNo: number) => void;
  /** Multi-select for «flytt valgte til slutt»: om linja er markert. */
  isMoveSelected?: boolean;
  /** Multi-select: toggle markering — statussirkelen blir trykkbar for ufullførte linjer. */
  onToggleMoveSelect?: (lineNo: number) => void;
  /** Strukturplukk: åpner telle-snarveien (vises kun på begrensede komponentlinjer). */
  onStructureCount?: (lineNo: number) => void;
  /** Slett en pall fra pall-lista på linja. Skjules når den ikke er satt. */
  onDeletePallet?: (sscc: string) => void;
  /** Slett en manuelt tillagt linje («Legg til vare»). Vises kun når line.isAdditionalLine (TW 36391640). */
  onDeleteAdditionalLine?: (lineNo: number) => void;
  /** Endre antall på en manuelt tillagt linje (TW 36393701). Vises kun når line.isAdditionalLine. */
  onEditAdditionalLine?: (lineNo: number) => void;
  /** picking.largeText (TW 36393697): større tekst og ikoner i linjelista. */
  largeText?: boolean;
  /**
   * Skjuler «Del linje». Settes i D-pak-modus, der pallene opprettes per skann og
   * manuell omfordeling ville gjort de allerede printede etikettene feil.
   */
  hideSplit?: boolean;
  /**
   * D-pak-modus. Av (standard) => uendret pall-visning med én chip per pall, slik
   * eksisterende kunder har i dag. På => kompakt teller med utvidbar detaljliste, som
   * er nødvendig når en linje kan ha 20+ paller.
   */
  dpakMode?: boolean;
}

export const PickingLineItem: React.FC<PickingLineItemProps> = ({
  line,
  index,
  isSelected,
  isActive,
  pallets,
  editingLocation,
  editingQuantity,
  newLocation,
  newQuantity,
  showPalletInfo,
  onLineClick,
  onLocationEdit,
  onQuantityEdit,
  onStartLocationEdit,
  onStartQuantityEdit,
  onLocationChange,
  onQuantityChange,
  onSplitLine,
  onPickLine,
  onUndoPicking,
  onShowPalletInfo,
  onMoveToEnd,
  isMoveSelected,
  onToggleMoveSelect,
  onStructureCount,
  onDeletePallet,
  onDeleteAdditionalLine,
  onEditAdditionalLine,
  hideSplit,
  dpakMode,
  largeText = false
}) => {
  const { t } = useTranslation();
  const [showFullItemNo, setShowFullItemNo] = React.useState(false);
  const [showPalletList, setShowPalletList] = React.useState(false);

  // Strukturvarer: «maks mulig sett»-begrensning fra applyStructureCaps. Når satt (og lavere
  // enn bestilt), plukker Plukk-knappen maks-antallet, og linjen viser en forklaring.
  const structureCapNote = getStructureCapNote(line, t);
  const effectivePickQty = structureCapNote !== undefined && line.structureMaxQty !== undefined
    ? line.structureMaxQty
    : line.qty;

  // Paller på linja. Beregnes i usePickingData (som kjenner D-pak-modus) og leveres
  // ferdig i line.splitAllocations — så matchingen finnes kun ett sted.
  const palletAllocations = React.useMemo(
    () => line.splitAllocations ?? [],
    [line.splitAllocations]
  );
  const uniformPalletQty = React.useMemo(
    () => getUniformPalletQuantity(palletAllocations.map(a => ({ quantity: a.qty }))),
    [palletAllocations]
  );
  const baseClasses = isActive
    ? 'border-blue-400 bg-blue-100 ring-1 ring-blue-200'
    : isSelected
    ? 'border-blue-500 bg-blue-50'
    : line.isFullyPicked
    ? 'border-green-400 bg-green-50 opacity-80'
    : line.isPartiallyPicked
    ? 'border-orange-400 bg-orange-50'
    : 'border-gray-200 hover:border-gray-300';
  // Markert for samlet flytting — synlig uansett øvrig status
  const moveSelectedClasses = isMoveSelected ? ' ring-2 ring-purple-400 border-purple-400' : '';

  // Statusikon; når multi-select er aktivt blir det en trykkbar markeringssirkel for ufullførte linjer
  const renderStatusIcon = (size: number) => {
    const icon = isMoveSelected ? (
      <CheckCircle2 className="text-purple-600" size={size} />
    ) : line.isFullyPicked ? (
      <CheckCircle2 className="text-green-600" size={size} />
    ) : line.isPartiallyPicked ? (
      <Circle className="text-orange-500 fill-current" size={size} />
    ) : (
      <Circle className="text-gray-400" size={size} />
    );
    if (onToggleMoveSelect && !line.isCompleted) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleMoveSelect(line.pListLineNo);
          }}
          className="p-2 -m-2 flex items-center justify-center"
          aria-label={t('warehouse.modules.picking.actions.selectForMove', {}, 'Marker for flytting')}
        >
          {icon}
        </button>
      );
    }
    return icon;
  };

  return (
    <div
      className={`p-2 border rounded-lg cursor-pointer transition-colors ${baseClasses}${moveSelectedClasses}`}
      onClick={() => onLineClick(index)}
    >
      {/* MOBILE: Stacked layout with tap-to-expand */}
      <div className="sm:hidden">
        {/* Row 1: Status icon + Item number (tap to expand) */}
        <div className="flex items-start gap-2 mb-1">
          <div className="flex-shrink-0 mt-0.5">
            {renderStatusIcon(largeText ? 22 : 16)}
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowFullItemNo(!showFullItemNo);
            }}
            className={`font-semibold ${largeText ? 'text-base' : 'text-xs'} text-gray-900 text-left flex-1 min-w-0 hover:text-blue-700 transition-colors`}
          >
            {showFullItemNo ? (
              <span>{line.itemNo}</span>
            ) : (
              <span className="truncate block">{line.itemNo}</span>
            )}
          </button>

          {/* Icons and badges */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Farlig gods (TW 36383256): varsel til plukkeren om at varen krever
                ADR-håndtering. Kompaktraden er en tett ikonstripe, så her står ikonet
                alene — den brede raden under viser ikon + tekst. */}
            {line.isDangerousGoods && (
              <TriangleAlert
                className="text-red-600 flex-shrink-0"
                size={12}
                aria-label={t('warehouse.modules.picking.labels.dangerousGoods', {}, 'Farlig gods')}
              />
            )}
            {line.allocationResID && !line.isFullyPicked && (
              <span className="bg-teal-100 text-teal-800 text-[10px] px-1 py-0.5 rounded font-semibold"
                    title={t('warehouse.modules.picking.labels.allocatedTooltip', {}, 'Allokert til salgsordre — skann allokeringsetiketten på varene')}>
                ALC-{line.allocationResID}
              </span>
            )}
            {line.image && <Camera className="text-blue-500" size={10} />}
            {line.itemType === 2 && (
              <Hash className={`${(line.serialNumberCount || 0) >= line.qty ? 'text-green-500' : 'text-amber-500'}`} size={10} />
            )}
            {line.itemType === 1 && (
              <Layers className={`${(line.batchNumberCount || 0) > 0 ? 'text-green-500' : 'text-amber-500'}`} size={10} />
            )}
          </div>
        </div>

        {/* Row 2: Description (tap to expand) */}
        {line.itemDescription && (
          <div className="mb-1 pl-6">
            <span className={`${largeText ? 'text-sm' : 'text-xs'} text-gray-600 block`}>{line.itemDescription}</span>
          </div>
        )}

        {/* Strukturvare: maks mulig sett-begrensning + telle-snarvei */}
        {structureCapNote && !line.isFullyPicked && (
          <div className="mb-1 pl-6 flex items-center gap-2">
            <span className="text-[10px] font-semibold text-amber-700 flex-1 min-w-0">⚠ {structureCapNote}</span>
            {onStructureCount && hasStructureDeficit(line) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStructureCount(line.pListLineNo);
                }}
                className="text-[10px] font-bold text-amber-900 bg-amber-100 active:bg-amber-200 px-2 py-1 rounded flex-shrink-0 min-h-[28px]"
              >
                {t('warehouse.modules.picking.structure.countLocation', {}, 'Tell lokasjon')}
              </button>
            )}
          </div>
        )}

        {/* Row 3: Location + Quantity + Actions */}
        <div className="flex items-center gap-2 pl-6">
          <span className={`${largeText ? 'text-base' : 'text-xs'} text-gray-600`}>{line.location}</span>
          <span className="text-gray-400 text-xs">•</span>
          <span className={`${largeText ? 'text-base' : 'text-xs'} font-medium ${line.isFullyPicked ? 'text-green-700' : line.isPartiallyPicked ? 'text-orange-700' : 'text-gray-700'}`}>
            {line.qtyPicked}/{line.qty}{line.unitTxt ? ` ${line.unitTxt}` : ''}
          </span>

          {/* Pall-info. D-pak-modus: kompakt teller som skalerer til mange paller.
              Ellers uendret visning av første pall, slik eksisterende kunder har i dag. */}
          {palletAllocations.length > 0 && (
            <>
              <span className="text-gray-400 text-xs">•</span>
              {dpakMode ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPalletList(!showPalletList);
                  }}
                  className="bg-purple-100 text-purple-700 px-1.5 py-1 rounded text-[10px] min-h-[28px] flex items-center gap-0.5"
                >
                  <Package2 size={10} />
                  {palletAllocations.length}
                  {showPalletList ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                </button>
              ) : (
                <span className="bg-purple-100 text-purple-700 px-1 py-0.5 rounded text-[10px]">
                  P{palletAllocations[0].palletId.slice(-4)}
                </span>
              )}
            </>
          )}

          {/* Flytt til slutt (kompakt) */}
          {onMoveToEnd && !line.isCompleted && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveToEnd(line.pListLineNo);
              }}
              className="ml-auto text-gray-400 active:text-gray-700 p-2 -my-1 min-h-[36px] min-w-[36px] flex items-center justify-center"
              aria-label={t('warehouse.modules.picking.actions.moveToEnd', {}, 'Flytt til slutt')}
            >
              <ChevronsDown size={largeText ? 22 : 16} />
            </button>
          )}

          {/* Endre antall på tilleggslinje (TW 36393701) — kun manuelt tillagte linjer */}
          {line.isAdditionalLine && onEditAdditionalLine && (
            <button
              onClick={(e) => { e.stopPropagation(); onEditAdditionalLine(line.pListLineNo); }}
              className={`${onMoveToEnd && !line.isCompleted ? '' : 'ml-auto'} p-1.5 rounded text-blue-600 border border-blue-200 min-h-[28px] min-w-[28px] flex items-center justify-center`}
              aria-label={t('warehouse.modules.picking.actions.editAdditionalLine', {}, 'Endre antall på tilleggslinje')}
              title={t('warehouse.modules.picking.actions.editAdditionalLine', {}, 'Endre antall på tilleggslinje')}
            >
              <Pencil size={largeText ? 18 : 14} />
            </button>
          )}

          {/* Slett tilleggslinje (TW 36391640) — kun manuelt tillagte linjer */}
          {line.isAdditionalLine && onDeleteAdditionalLine && (
            <button
              onClick={(e) => { e.stopPropagation(); onDeleteAdditionalLine(line.pListLineNo); }}
              className={`${(onMoveToEnd && !line.isCompleted) || onEditAdditionalLine ? '' : 'ml-auto'} p-1.5 rounded text-red-600 border border-red-200 min-h-[28px] min-w-[28px] flex items-center justify-center`}
              aria-label={t('warehouse.modules.picking.actions.deleteAdditionalLine', {}, 'Slett tilleggslinje')}
              title={t('warehouse.modules.picking.actions.deleteAdditionalLine', {}, 'Slett tilleggslinje')}
            >
              <Trash2 size={largeText ? 18 : 14} />
            </button>
          )}

          {/* Actions - compact. Allokerte linjer (TW 36362298) må bekreftes med ALC-skann,
              så Plukk-knappen skjules for dem (Angre vises fortsatt når linjen er fullført). */}
          {(line.isCompleted || !line.allocationResID) &&
           ((!line.requireItemScan && !line.requireLocationScan) || line.binCode === '[X]' || line.isCompleted || line.stcTransactionsYesNo === false) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (line.isCompleted) {
                  onUndoPicking(line.pListLineNo);
                } else {
                  onPickLine(line.pListLineNo, effectivePickQty);
                }
              }}
              className={`${onMoveToEnd && !line.isCompleted ? '' : 'ml-auto'} ${largeText ? 'px-3 py-2 text-sm min-h-[40px]' : 'px-2 py-1 text-[10px] min-h-[28px]'} rounded font-medium ${
                line.isCompleted
                  ? 'bg-red-600 text-white'
                  : line.binCode === '[X]'
                  ? 'bg-amber-500 text-white'
                  : 'bg-blue-600 text-white'
              }`}
            >
              {line.isCompleted
                ? t('warehouse.modules.picking.actions.undo')
                : line.binCode === '[X]'
                ? t('warehouse.modules.picking.actions.shortage', {}, 'Manko')
                : t('warehouse.modules.picking.pick')}
            </button>
          )}
        </div>
      </div>

      {/* DESKTOP: Full layout */}
      <div className="hidden sm:flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {/* Status icon */}
          <div className="flex-shrink-0 mt-0.5">
            {renderStatusIcon(largeText ? 24 : 18)}
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-0.5">
              <span className={`font-bold text-gray-900 ${largeText ? 'text-lg' : 'text-sm'}`}>{line.itemNo}</span>
              <span className={`${largeText ? 'text-base' : 'text-sm'} text-gray-700 truncate`}>{line.itemDescription}</span>
              {/* Farlig gods: alltid synlig tekst, ikke bare ikon — plukkbildet brukes på
                  nettbrett, der det ikke finnes hover for å forklare et symbol. */}
              {line.isDangerousGoods && (
                <span className="inline-flex items-center gap-1 bg-red-100 text-red-800 border border-red-300 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase">
                  <TriangleAlert size={11} className="flex-shrink-0" />
                  {t('warehouse.modules.picking.labels.dangerousGoods', {}, 'Farlig gods')}
                </span>
              )}
              {isActive && (
                <span className="bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium animate-pulse">
                  {t('warehouse.modules.picking.labels.active')}
                </span>
              )}
              {line.allocationResID && !line.isFullyPicked && (
                <span className="bg-teal-100 text-teal-800 text-[10px] px-1.5 py-0.5 rounded font-semibold"
                      title={t('warehouse.modules.picking.labels.allocatedTooltip', {}, 'Allokert til salgsordre — skann allokeringsetiketten på varene')}>
                  {t('warehouse.modules.picking.labels.allocated', {}, 'Allokert')} · ALC-{line.allocationResID}
                </span>
              )}
              {line.requireLocationScan && <MapPin className="text-orange-500" size={12} />}
              {line.image && <Camera className="text-blue-500" size={12} />}
              {line.itemType === 2 && (
                <span className={`flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded ${
                  (line.serialNumberCount || 0) >= line.qty ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`} title="Krever serienummer">
                  <Hash size={10} />
                  {line.serialNumberCount || 0}/{Math.floor(line.qty)}
                </span>
              )}
              {line.itemType === 1 && (
                <span className={`flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded ${
                  (line.batchNumberCount || 0) > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`} title="Krever batchnummer">
                  <Layers size={10} />
                  {line.batchNumberCount || 0}
                </span>
              )}
            </div>
            <div className={`flex flex-wrap items-center gap-2 ${largeText ? 'text-sm' : 'text-xs'} text-gray-500`}>
              <span>#{line.pListLineNo}</span>
              <span>•</span>
              <span>{t('warehouse.modules.picking.labels.ordered')}: {line.qty}{line.unitTxt ? ` ${line.unitTxt}` : ''}</span>
              <span>•</span>
              <span>{t('warehouse.modules.picking.picked')}: {line.qtyPicked}</span>
              <span>•</span>
              <span>{line.location}</span>
            </div>
            {structureCapNote && !line.isFullyPicked && (
              <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 mt-0.5">
                <span>⚠ {structureCapNote}</span>
                {onStructureCount && hasStructureDeficit(line) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onStructureCount(line.pListLineNo);
                    }}
                    className="text-xs font-bold text-amber-900 bg-amber-100 hover:bg-amber-200 px-2 py-0.5 rounded flex-shrink-0"
                  >
                    {t('warehouse.modules.picking.structure.countLocation', {}, 'Tell lokasjon')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/*
            D-pak-modus: én kompakt teller. Den erstatter chip-per-pall-rekka, som med mange
            paller (opptil 21 på én linje) beslagla hele bredden i denne flex-shrink-0-
            containeren og klemte varenavn/metadata til null. Detaljene ligger i en utvidbar
            liste under linja.

            Uten D-pak-modus beholdes chip-rekka nøyaktig som den var, slik at eksisterende
            kunder ikke får noen visuell endring. De har i praksis få paller per linje.
          */}
          {dpakMode && palletAllocations.length > 1 ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowPalletList(!showPalletList);
              }}
              className="bg-purple-100 text-purple-700 hover:bg-purple-200 px-2 py-1 rounded text-xs flex items-center gap-1 whitespace-nowrap min-h-[32px]"
              title={t('warehouse.modules.picking.pallets.showDetails', {}, 'Vis paller')}
            >
              <Package2 size={12} />
              <span>
                {t('warehouse.modules.picking.pallets.count', { '0': String(palletAllocations.length) }, '{0} paller')}
                {uniformPalletQty !== null && ` à ${uniformPalletQty}`}
              </span>
              {showPalletList ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          ) : palletAllocations.length > 1 ? (
            <div className="flex gap-1">
              {palletAllocations.map((allocation, idx) => (
                <div
                  key={idx}
                  className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs cursor-pointer hover:bg-purple-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowPalletInfo(showPalletInfo === allocation.palletId ? null : allocation.palletId);
                  }}
                >
                  P{allocation.palletId.slice(-4)}({allocation.qty})
                </div>
              ))}
              <SplitSquareHorizontal className="text-purple-600 ml-0.5" size={14} />
            </div>
          ) : palletAllocations.length === 1 ? (
            <div
              className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs cursor-pointer hover:bg-purple-200"
              onClick={(e) => {
                e.stopPropagation();
                const palletId = palletAllocations[0].palletId;
                onShowPalletInfo(showPalletInfo === palletId ? null : palletId);
              }}
            >
              P{palletAllocations[0].palletId.slice(-4)}
            </div>
          ) : null}

          {onMoveToEnd && !line.isCompleted && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveToEnd(line.pListLineNo);
              }}
              className="text-gray-500 hover:text-gray-700 p-1"
              title={t('warehouse.modules.picking.actions.moveToEnd', {}, 'Flytt til slutt')}
              aria-label={t('warehouse.modules.picking.actions.moveToEnd', {}, 'Flytt til slutt')}
            >
              <ChevronsDown size={14} />
            </button>
          )}

          {!hideSplit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSplitLine(line.pListLineNo);
              }}
              className="text-gray-500 hover:text-gray-700 p-1"
              title={t('warehouse.modules.picking.actions.split')}
            >
              <SplitSquareHorizontal size={14} />
            </button>
          )}

          {/* Endre antall på tilleggslinje (TW 36393701) — kun manuelt tillagte linjer */}
          {line.isAdditionalLine && onEditAdditionalLine && (
            <button
              onClick={(e) => { e.stopPropagation(); onEditAdditionalLine(line.pListLineNo); }}
              className="px-2 py-1 rounded text-xs text-blue-600 border border-blue-200 hover:bg-blue-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={t('warehouse.modules.picking.actions.editAdditionalLine', {}, 'Endre antall på tilleggslinje')}
              title={t('warehouse.modules.picking.actions.editAdditionalLine', {}, 'Endre antall på tilleggslinje')}
            >
              <Pencil size={largeText ? 18 : 14} />
            </button>
          )}

          {/* Slett tilleggslinje (TW 36391640) — kun manuelt tillagte linjer */}
          {line.isAdditionalLine && onDeleteAdditionalLine && (
            <button
              onClick={(e) => { e.stopPropagation(); onDeleteAdditionalLine(line.pListLineNo); }}
              className="px-2 py-1 rounded text-xs text-red-600 border border-red-200 hover:bg-red-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={t('warehouse.modules.picking.actions.deleteAdditionalLine', {}, 'Slett tilleggslinje')}
              title={t('warehouse.modules.picking.actions.deleteAdditionalLine', {}, 'Slett tilleggslinje')}
            >
              <Trash2 size={largeText ? 18 : 14} />
            </button>
          )}

          {/* Allokerte linjer (TW 36362298) må bekreftes med ALC-skann — Plukk-knappen skjules. */}
          {(line.isCompleted || !line.allocationResID) &&
           ((!line.requireItemScan && !line.requireLocationScan) || line.binCode === '[X]' || line.isCompleted || line.stcTransactionsYesNo === false) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (line.isCompleted) {
                  onUndoPicking(line.pListLineNo);
                } else {
                  onPickLine(line.pListLineNo, effectivePickQty);
                }
              }}
              className={`px-2 py-1 rounded text-xs transition-colors ${
                line.isCompleted
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : line.binCode === '[X]'
                  ? 'bg-amber-500 text-white hover:bg-amber-600'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {line.isCompleted
                ? t('warehouse.modules.picking.actions.undo')
                : line.binCode === '[X]'
                ? t('warehouse.modules.picking.actions.shortage', {}, 'Manko')
                : t('warehouse.modules.picking.pick')}
            </button>
          )}
        </div>
      </div>

      {/*
        Pall-detaljer. Vises når telleren trykkes, og ligger UNDER linja i full bredde —
        derfor påvirker antall paller aldri bredden på varenavn og metadata. Én rad per
        pall med SSCC, antall, batch og holdbarhet.
      */}
      {dpakMode && showPalletList && palletAllocations.length > 0 && (
        <div className="mt-2 pt-2 border-t border-purple-200 space-y-1">
          {palletAllocations.map((allocation) => (
            <div
              key={allocation.palletId}
              className="flex items-center gap-2 text-xs bg-purple-50 rounded px-2 py-1.5"
            >
              <Package2 size={12} className="text-purple-600 flex-shrink-0" />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onShowPalletInfo(showPalletInfo === allocation.palletId ? null : allocation.palletId);
                }}
                className="font-mono text-purple-800 hover:underline flex-shrink-0"
                title={allocation.palletId}
              >
                …{allocation.palletId.slice(-8)}
              </button>
              <span className="font-medium text-gray-800 flex-shrink-0">{allocation.qty}</span>
              {allocation.itemBatchNo && (
                <span className="text-gray-600 truncate">{allocation.itemBatchNo}</span>
              )}
              {allocation.itemBatchExpDate && (
                <span className="text-gray-600 whitespace-nowrap flex-shrink-0">
                  {formatExpiryDate(allocation.itemBatchExpDate)}
                </span>
              )}
              {onDeletePallet && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const message = t(
                      'warehouse.modules.picking.pallets.deleteConfirm',
                      { '0': allocation.palletId, '1': String(allocation.qty) },
                      'Slette pall {0}?\n\nAntallet ({1}) trekkes fra plukket antall på linja.'
                    );
                    if (window.confirm(message)) {
                      onDeletePallet(allocation.palletId);
                    }
                  }}
                  className="ml-auto text-red-500 hover:text-red-700 p-1 flex-shrink-0 min-h-[32px] min-w-[32px] flex items-center justify-center"
                  aria-label={t('warehouse.modules.picking.pallets.delete', {}, 'Slett pall')}
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
